import type { RowData } from "@tanstack/react-table";
import { getIsSummaryRow } from "../cells/summaryRow";
import { resolveColumnMeta } from "../columns/columnKinds";
import type {
    DataGridCell,
    DataGridEditor,
    DataGridTable,
} from "../columns/types";
import type {
    DataGridCellAddress,
    DataGridCellValue,
    DataGridInstance,
    DataGridListEditingOptions,
} from "../types";
import { toEditorValue } from "./editorValues";

export interface EditableCell<TData extends RowData> {
    cell: DataGridCell<TData>;
    editor: DataGridEditor;
}

export const getCell = <TData extends RowData>(
    table: DataGridTable<TData>,
    { rowId, columnId }: DataGridCellAddress,
): DataGridCell<TData> | undefined =>
    table.getPrePaginatedRowModel().rowsById[rowId]?.getAllCellsByColumnId()[
        columnId
    ];

/** A cell from every row, including rows a search or page hides. */
export const getCoreCell = <TData extends RowData>(
    table: DataGridTable<TData>,
    { rowId, columnId }: DataGridCellAddress,
): DataGridCell<TData> | undefined =>
    table.getCoreRowModel().rowsById[rowId]?.getAllCellsByColumnId()[columnId];

/**
 * A cell's value as its editor commits it, read from every row (search and
 * pages don't hide it); `undefined` for a missing row or a column without an
 * editor.
 */
export const readEditorValue = <TData extends RowData>(
    table: DataGridTable<TData>,
    address: DataGridCellAddress,
): DataGridCellValue | undefined => {
    const cell = getCoreCell(table, address);
    if (!cell) {
        return undefined;
    }
    const { editor } = resolveColumnMeta(cell.column.columnDef, cell.column.id);
    return editor
        ? toEditorValue({ editor, value: cell.getValue() })
        : undefined;
};

/**
 * Separator rows, group rows, and a `parentRows: "summary"` row's cells
 * besides its label: they show a label, a summary, or nothing, so they never
 * edit. A tree column with a `summary` shows the summary there, so it doesn't
 * edit either.
 */
export const isLabelRowCell = <TData extends RowData>(
    instance: DataGridInstance<TData>,
    cell: DataGridCell<TData>,
) => {
    if (
        instance.rowKinds.isSeparator(cell.row.original) ||
        cell.row.getIsGrouped()
    ) {
        return true;
    }
    if (
        !getIsSummaryRow({
            hasParentSummaries: instance.hasParentSummaries,
            row: cell.row,
            isParentRow: instance.rowKinds.isParentRow,
        })
    ) {
        return false;
    }
    const meta = resolveColumnMeta(cell.column.columnDef, cell.column.id);
    return !meta.tree || meta.hasSummary;
};

/** The cell's editor when the column has one, the row is a data row, and the consumer allows it. */
export const getEditableCell = <TData extends RowData>(
    instance: DataGridInstance<TData>,
    cell: DataGridCell<TData> | undefined,
): EditableCell<TData> | null => {
    if (!instance.isEditable || !cell || isLabelRowCell(instance, cell)) {
        return null;
    }
    const { editor } = resolveColumnMeta(cell.column.columnDef, cell.column.id);
    if (!editor) {
        return null;
    }
    const isAllowed =
        instance.optionsRef.current.editing?.isCellEditable?.({
            row: cell.row.original,
            columnId: cell.column.id,
        }) ?? true;
    return isAllowed ? { cell, editor } : null;
};

interface ListAccessInput<TData extends RowData> {
    instance: DataGridInstance<TData>;
    cell: DataGridCell<TData>;
    /** The rules to apply: a render's `instance.listEditing`, or the latest options in event handlers. */
    listEditing: DataGridListEditingOptions<TData> | undefined;
}

/** `getListEditableCell` against the given rules. */
export const isListCellEditable = <TData extends RowData>({
    instance,
    cell,
    listEditing,
}: ListAccessInput<TData>) =>
    instance.hasListEditing &&
    !isLabelRowCell(instance, cell) &&
    !instance.rowKinds.isDraft(cell.row.original) &&
    resolveColumnMeta(cell.column.columnDef, cell.column.id).isListEditable &&
    (listEditing?.isCellEditable?.({
        row: cell.row.original,
        columnId: cell.column.id,
    }) ??
        true);

/** List editing: the cell when its column has `edit`, the row is a saved data row, and the latest rules allow it. */
export const getListEditableCell = <TData extends RowData>(
    instance: DataGridInstance<TData>,
    cell: DataGridCell<TData> | undefined,
): DataGridCell<TData> | null =>
    cell &&
    isListCellEditable({
        instance,
        cell,
        listEditing: instance.optionsRef.current.listEditing,
    })
        ? cell
        : null;

/** Why a list-editable column's cell isn't editable on its row (drafts give none); read only once `isListCellEditable` said no. */
export const getListReadOnlyReason = <TData extends RowData>({
    instance,
    cell,
    listEditing,
}: ListAccessInput<TData>) =>
    instance.rowKinds.isDraft(cell.row.original)
        ? undefined
        : listEditing?.getReadOnlyReason?.({
              row: cell.row.original,
              columnId: cell.column.id,
          });
